"use client";

import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Image from "next/image";
import type { AxiosError } from "axios";

import axiosInstance from "@/utils/axiosInstance";
import { useAuth } from "@/hook/useAuth";
import {
	getUploadedDocument,
	getUserDetail,
	updateUserDetails,
} from "@/store/feature/user/userApi";
import { saveEligibilityDetails } from "@/store/feature/ai-course-finder/aiCourseApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import useToastNotification from "@/hook/useToastNotification";
import DropzoneUploader from "./DocumentUpload/DropzoneUploader";
import FilePreviewCard from "./DocumentUpload/FilePreviewCard";
import DocumentPreviewModal from "./DocumentUpload/DocumentPreviewModal";
import ConfirmExtractedDetails from "./DocumentUpload/ConfirmExtractedDetails";
import { capitalFirstLetter } from "@/utils/capitalFirstLetter";
import { formatToDDMMYYYY } from "@/utils/formatToDDMMYYY";
import { parseDate } from "@/utils/parseDate";
import { parseAddress } from "@/utils/parseAddress";

interface DocumentUploadOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	documentName: string;
	handleClose?: () => void;
	onUploadSuccess?: () => void;
	showTitle?: boolean;
	from?: string;
	applicationId?: string;
	skipOCR?: boolean;
	studentId?: string;
	onFileAccepted?: (file: File) => void | Promise<void>;
}

export interface SectionScores {
	[key: string]: string;
}

export interface SubjectSpecificMarks {
	[key: string]: string | number | Record<string, string | number>;
}

export interface ExtractedData {
	[key: string]:
		| string
		| number
		| SectionScores
		| SubjectSpecificMarks
		| Subject[]
		| undefined
		| null;
	// Common fields
	institution?: string;
	board?: string;
	yearOfPassing?: string;
	fieldOfStudy?: string;
	subjectSpecificMarks?: SubjectSpecificMarks;
	// Marksheet specific fields
	studentName?: string;
	rollNumber?: string;
	examinationName?: string;
	mothersName?: string;
	fathersName?: string;
	dateOfBirth?: string;
	subjects?: Subject[];
	cgpaOrPercentageValue?: string;
	outOf?: string;
	totalMarksObtained?: string;
	// Marksheet type fields
	marksheet_type?: string;
	school_name?: string;
	normalized_board_name?: string;
	passout_year?: string;
	student_name?: string;
	roll_number?: string;
	examination_name?: string;
	mothers_name?: string;
	fathers_or_guardians_name?: string;
	date_of_birth?: string;
	affiliated_college?: string;
	stream_or_group?: string;
	grand_total_marks_obtained?: string | number;
	grand_total_marks_obtained_out_of?: string | number;
	// Bachelor's degree specific fields
	institution_name?: string;
	degree_name?: string;
	degreeName?: string;
	overall_cgpa?: number | string | null;
	overall_cgpa_out_of?: number | string;
	overall_marks?: number | string;
	overall_marks_out_of?: number | string;
	semesterwise_sgpa_or_marks?: any[];
	// Passport fields
	passportNumber?: string;
	passport_number?: string;
	countryOfIssue?: string;
	expiryDate?: string;
	date_of_expiry?: string;
	given_name?: string;
	surname?: string;
	surename?: string;
	name_of_father_or_legal_guardian?: string;
	address?: string;
	gender?: string | null;
	// English proficiency fields
	test_type?: string;
	overall?: string;
	overall_score?: string | number;
	reading?: string;
	writing?: string;
	speaking?: string;
	listening?: string;
	section_scores?: SectionScores;
	test_date?: string;
	valid_till_date?: string;
	// MOI specific fields
	board_name?: string;
	english_marks?: string | number;
}

interface Subject {
	subject: string;
	marks_out_of: string;
	total_marks_obtained: string;
}

interface ParsedName {
	firstName: string;
	middleName: string;
	lastName: string;
}

const OCR_SUPPORTED_DOC_TYPES = new Set([
	"10th_marksheet",
	"12th_marksheet",
	"bachelor_marksheet",
	"english_proficiency",
	"passport",
]);

// Helper function to calculate bachelor grading
const calculateBachelorGrading = (
	extractedData: ExtractedData,
): {
	gradeType: string;
	value: number;
	outOf: number;
} => {
	// PRIORITY 1: Check if CGPA is available
	if (
		extractedData?.overall_cgpa !== null &&
		extractedData?.overall_cgpa !== undefined &&
		extractedData?.overall_cgpa !== ""
	) {
		const cgpa = Number(extractedData.overall_cgpa);
		const cgpaOutOf = Number(extractedData?.overall_cgpa_out_of) || 10;

		const gradeTypeMap: { [key: number]: string } = {
			3: "CGPA out of 3",
			4: "CGPA out of 4",
			5: "CGPA out of 5",
			7: "CGPA out of 7",
			9: "CGPA out of 9",
			10: "CGPA out of 10",
		};

		return {
			gradeType: gradeTypeMap[cgpaOutOf] || "CGPA out of 10",
			value: cgpa,
			outOf: cgpaOutOf,
		};
	}

	// PRIORITY 2: If overall marks are available, calculate percentage
	if (
		extractedData?.overall_marks !== null &&
		extractedData?.overall_marks !== undefined &&
		extractedData?.overall_marks_out_of !== null &&
		extractedData?.overall_marks_out_of !== undefined
	) {
		const marksObtained = Number(extractedData.overall_marks);
		const marksOutOf = Number(extractedData.overall_marks_out_of);

		if (marksOutOf > 0) {
			const percentage =
				Math.round((marksObtained / marksOutOf) * 100 * 100) / 100;

			return {
				gradeType: "Percentage",
				value: percentage,
				outOf: 100,
			};
		}
	}

	// Default fallback
	return {
		gradeType: "Percentage",
		value: 0,
		outOf: 100,
	};
};

const mapStreamToFieldOfStudy = (stream: string): string => {
	const normalizedStream = (stream || "").toLowerCase().trim();
	if (normalizedStream.includes("commerce")) return "Commerce";
	if (
		normalizedStream.includes("non-medical") ||
		normalizedStream.includes("non medical")
	) {
		return "Non-Medical";
	}
	if (
		normalizedStream.includes("super-medical") ||
		normalizedStream.includes("super medical") ||
		normalizedStream.includes("pcmb")
	) {
		return "Super-Medical";
	}
	if (
		normalizedStream.includes("medical") ||
		normalizedStream.includes("biology")
	) {
		return "Medical";
	}
	if (
		normalizedStream.includes("arts") ||
		normalizedStream.includes("humanities")
	) {
		return "Arts-&-Humanities";
	}
	return "";
};

const buildSubjectMarks = (
	subjects: Subject[],
	fieldOfStudy: string,
): SubjectSpecificMarks => {
	const stream = fieldOfStudy.toLowerCase();
	let coreSubjects: string[] = [];

	if (stream === "commerce") {
		coreSubjects = ["Business Studies", "Accounting", "Economics", "English"];
	} else if (stream === "medical") {
		coreSubjects = ["Physics", "Chemistry", "Biology", "English"];
	} else if (stream === "super-medical") {
		coreSubjects = [
			"Physics",
			"Chemistry",
			"Biology",
			"English",
			"Mathematics",
		];
	} else if (stream === "non-medical") {
		coreSubjects = ["Physics", "Chemistry", "Mathematics", "English"];
	} else if (stream === "arts-&-humanities") {
		coreSubjects = ["English"];
	} else {
		coreSubjects = ["Physics", "Chemistry", "English"];
	}

	const normalizeSubject = (name: string) =>
		name
			.toLowerCase()
			.replace(/[^a-z0-9]/g, " ")
			.trim();

	const subjectSpecificMarks: SubjectSpecificMarks = {
		additionalSubject: {},
	};

	const additionalSubjectMap: Record<string, string | number> = {};

	subjects.forEach((subjectItem) => {
		const ocrName = subjectItem.subject || "";
		const marks = subjectItem.total_marks_obtained || "";
		const normalizedOcr = normalizeSubject(ocrName);

		const matchedCore = coreSubjects.find((coreSubject) =>
			normalizeSubject(coreSubject)
				.split(" ")
				.some((word) => word.length > 3 && normalizedOcr.includes(word)),
		);

		if (matchedCore) {
			subjectSpecificMarks[matchedCore] = marks;
		} else {
			const cleanName = ocrName.replace(/^\*+/, "").trim();
			if (cleanName) {
				additionalSubjectMap[cleanName] = marks;
			}
		}
	});

	subjectSpecificMarks.additionalSubject = additionalSubjectMap;
	return subjectSpecificMarks;
};

// Helper function to parse extracted names
const parseExtractedName = (extractedName: string): ParsedName => {
	const normalized = extractedName
		.toLowerCase()
		.trim()
		.replace(/[^a-z\s]/g, "")
		.replace(/\s+/g, " ");

	const parts = normalized.split(" ").filter(Boolean);

	if (parts.length === 0) {
		return { firstName: "", middleName: "", lastName: "" };
	}
	if (parts.length === 1) {
		return { firstName: parts[0], middleName: "", lastName: "" };
	}
	if (parts.length === 2) {
		return { firstName: parts[0], middleName: "", lastName: parts[1] };
	}
	// 3 or more parts: first is firstName, last is lastName, middle parts are middleName
	return {
		firstName: parts[0],
		middleName: parts.slice(1, -1).join(" "),
		lastName: parts[parts.length - 1],
	};
};

export default function DocumentUploadOverlay({
	isOpen,
	onClose,
	documentName,
	showTitle = true,
	onUploadSuccess,
	from,
	applicationId,
	skipOCR = false,
	studentId,
	onFileAccepted,
}: DocumentUploadOverlayProps) {
	const dispatch = useAppDispatch();
	const { user } = useAuth();
	const { showToast } = useToastNotification();
	const { userDetails } = useAppSelector((state) => state.userProfile);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isStatusUpdating, setIsStatusUpdating] = useState(false);
	const [error, setError] = useState<string>("");

	const [showPreview, setShowPreview] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const [extractedData, setExtractedData] = useState<ExtractedData | null>(
		null,
	);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [editErrors, setEditErrors] = useState<Record<string, string>>({});

	const fileInputRef = useRef<HTMLInputElement>(
		null as unknown as HTMLInputElement,
	);

	const triggerFileInput = () => fileInputRef.current?.click();
	const removeFile = () => {
		setSelectedFile(null);
		setError("");
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const resetOverlayState = () => {
		removeFile();
		setDragActive(false);
		setError("");
		setShowPreview(false);
		setPreviewUrl(null);
		setExtractedData(null);
		setShowConfirmPopup(false);
		setEditErrors({});
	};

	useEffect(() => {
		if (!isOpen) {
			resetOverlayState();
		}
	}, [isOpen]);

	useEffect(() => {
		resetOverlayState();
	}, [documentName]);

	const handleFile = (file: File) => {
		const allowedTypes = [
			"application/pdf",
			"image/jpeg",
			"image/png",
			"application/msword",
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			"application/vnd.ms-excel",
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		];

		if (file.size > 5 * 1024 * 1024) {
			setError("File is too large. Maximum file size is 5MB.");
		} else if (!allowedTypes.includes(file.type)) {
			setError(
				"Invalid file type. Only PDF, DOCX, PNG, JPG files are allowed.",
			);
		} else {
			setSelectedFile(file);
			setError("");
		}
	};

	const getPresignedUrl = async (file: File) => {
		const name = documentName.replace(/\s+/g, "_").toLowerCase();
		const payload = [
			{
				fileName: file.name,
				documentType: name.split(".").pop(),
				mimeType: file.type,
				userType: "student",
				userId: user?.uid,
				studentId: studentId,
				fileSize: file.size,
			},
		];

		const response = await axiosInstance.post(
			"/documents/upload/get-presigned-url",
			payload,
		);
		return {
			presignedUrl: response.data[0].presignedUrl,
			documentId: response.data[0].documentId,
		};
	};

	const uploadFileToFirebase = async () => {
		try {
			const { presignedUrl, documentId } = await getPresignedUrl(
				selectedFile as File,
			);
			await fetch(presignedUrl, {
				method: "PUT",
				body: selectedFile,
				headers: {
					"Content-Type": selectedFile?.type || "application/octet-stream",
				},
			});
			await markStatusTrue(
				documentName.toLowerCase().replace(/\s+/g, "_"),
				documentId,
			);
		} catch (error) {
			console.error("Upload error:", error);
			throw error;
		}
	};

	const errorCheck = () => {
		const docNameLower = documentName.toLowerCase();

		if (docNameLower.includes("10th")) {
			return "This document does not appear to be a 10th marksheet.";
		}

		if (docNameLower.includes("12th")) {
			return "This document does not appear to be a 12th marksheet.";
		}

		if (docNameLower.includes("bachelor")) {
			return "This document does not appear to be a valid bachelor's degree marksheet.";
		}

		if (docNameLower.includes("english")) {
			return "This document does not appear to be a valid English proficiency test certificate. Please upload a valid IELTS, DUOLINGO, PTE, or TOEFL certificate.";
		}

		if (docNameLower === "passport") {
			return "This document does not appear to be a valid passport.";
		}

		return null;
	};

	const markStatusTrue = async (docType: string, docId: string) => {
		try {
			await axiosInstance.patch(`/documents/update/${docId}`, {
				status: "under_review",
				docType,
			});
		} catch (error) {
			console.error("Status update failed:", error);
			throw error;
		}
	};

	const extractDetails = async (selectedFile: File, docType: string) => {
		setIsStatusUpdating(true);
		setError("");
		try {
			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("document_type", docType);
			const response = await axiosInstance.post(
				`documents/ocr-extraction`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);

			const extracted = response.data.data || [];

			// For 10th and 12th marksheets, match by marksheet_type
			if (docType === "10th_marksheet" || docType === "12th_marksheet") {
				const matchedData = extracted.find(
					(item: ExtractedData) => item.marksheet_type === docType,
				);

				if (matchedData) {
					const subjects = matchedData.subjects || [];
					const grandTotalObtained = parseInt(
						(matchedData.grand_total_marks_obtained as string) || "",
					);
					const grandTotalOutOf = parseInt(
						(matchedData.grand_total_marks_obtained_out_of as string) || "",
					);

					let totalMarksObtained: number;
					let totalMarksOutOf: number;

					if (
						!isNaN(grandTotalObtained) &&
						!isNaN(grandTotalOutOf) &&
						grandTotalOutOf > 0
					) {
						totalMarksObtained = grandTotalObtained;
						totalMarksOutOf = grandTotalOutOf;
					} else {
						totalMarksObtained = 0;
						totalMarksOutOf = 0;
						subjects.forEach((subject: Subject) => {
							const marksObtained = parseInt(subject.total_marks_obtained) || 0;
							const marksOutOf = parseInt(subject.marks_out_of) || 0;

							totalMarksObtained += marksObtained;
							totalMarksOutOf += marksOutOf;
						});
					}

					const percentage =
						totalMarksOutOf > 0
							? Math.round((totalMarksObtained / totalMarksOutOf) * 100 * 100) /
								100
							: 0;

					const mappedStream =
						docType === "12th_marksheet"
							? mapStreamToFieldOfStudy(
									(matchedData.stream_or_group as string) || "",
								)
							: "";
					const mappedSubjectMarks =
						docType === "12th_marksheet"
							? buildSubjectMarks(subjects, mappedStream)
							: { additionalSubject: {} };

					const transformedData: ExtractedData = {
						...matchedData,
						institution: capitalFirstLetter(matchedData.school_name || ""),
						board: matchedData.normalized_board_name || "",
						yearOfPassing: matchedData.passout_year || "",
						studentName: capitalFirstLetter(matchedData.student_name || ""),
						rollNumber: matchedData.roll_number || "",
						examinationName: matchedData.examination_name || "",
						mothersName: capitalFirstLetter(matchedData.mothers_name || ""),
						fathersName: capitalFirstLetter(
							matchedData.fathers_or_guardians_name || "",
						),
						dateOfBirth: matchedData.date_of_birth || "",
						subjects: subjects,
						cgpaOrPercentageValue: percentage.toString(),
						outOf: totalMarksOutOf.toString(),
						totalMarksObtained: totalMarksObtained.toString(),
						fieldOfStudy: mappedStream,
						subjectSpecificMarks: mappedSubjectMarks,
					};

					setExtractedData(transformedData);
					setShowConfirmPopup(true);
				} else {
					const customError = errorCheck();
					setError(
						customError || "Could not find matching marksheet data. Try again.",
					);
				}
			}
			// For bachelor's degree marksheet - transform to match expected format
			else if (docType.includes("bachelor")) {
				if (extracted.length > 0) {
					const bachelorData = extracted[0] as ExtractedData;
					const grading = calculateBachelorGrading(bachelorData);

					const transformedData: ExtractedData = {
						...bachelorData,
						institution: capitalFirstLetter(
							(
								bachelorData.institution_name ||
								bachelorData.institution ||
								""
							).toString(),
						),
						degreeName: capitalFirstLetter(
							(
								bachelorData.degree_name ||
								bachelorData.degreeName ||
								""
							).toString(),
						),
						yearOfPassing: (
							bachelorData.passout_year ||
							bachelorData.yearOfPassing ||
							""
						).toString(),
						studentName: capitalFirstLetter(
							(
								bachelorData.student_name ||
								bachelorData.studentName ||
								""
							).toString(),
						),
						rollNumber: (
							bachelorData.roll_number ||
							bachelorData.rollNumber ||
							""
						).toString(),
						cgpaOrPercentageValue: grading.value.toString(),
						outOf: grading.outOf.toString(),
					};

					setExtractedData(transformedData);
					setShowConfirmPopup(true);
				} else {
					const customError = errorCheck();
					setError(
						customError ||
							"Could not extract bachelor's degree data. Try again.",
					);
				}
			}
			// For passport
			else if (docType === "passport") {
				if (extracted.length > 0) {
					const passportData = extracted[0];

					// Store raw passport data
					setExtractedData(passportData);
					setShowConfirmPopup(true);
				} else {
					const customError = errorCheck();
					setError(
						customError || "Could not extract passport data. Try again.",
					);
				}
			}
			// For English proficiency
			else if (docType === "english_proficiency") {
				if (extracted.length > 0) {
					const englishData = extracted[0];

					// Calculate valid_till_date (test_date + 2 years)
					let validTillDate = "";
					if (englishData.test_date) {
						const date = parseDate(String(englishData.test_date));
						if (date && !isNaN(date.getTime())) {
							const validDate = new Date(date);
							validDate.setFullYear(validDate.getFullYear() + 2);
							validTillDate = formatToDDMMYYYY(validDate);
						}
					}

					const sectionScores: SectionScores = {
						reading:
							englishData?.section_scores?.reading ??
							englishData?.reading ??
							"",
						writing:
							englishData?.section_scores?.writing ??
							englishData?.writing ??
							"",
						speaking:
							englishData?.section_scores?.speaking ??
							englishData?.speaking ??
							"",
						listening:
							englishData?.section_scores?.listening ??
							englishData?.listening ??
							"",
					};

					setExtractedData({
						...englishData,
						overall_score:
							englishData?.overall_score ?? englishData?.overall ?? "",
						section_scores: sectionScores,
						valid_till_date: validTillDate,
					});
					setShowConfirmPopup(true);
				} else {
					const customError = errorCheck();
					setError(
						customError ||
							"Could not extract English proficiency data. Try again.",
					);
				}
			} else {
				// For other document types, use the first item as before
				if (extracted.length > 0) {
					setExtractedData(extracted[0]);
					setShowConfirmPopup(true);
				} else {
					const customError = errorCheck();
					setError(
						customError ||
							"No data could be extracted from the document. Please try again.",
					);
				}
			}
		} catch (error) {
			console.error("Status update failed:", error);

			let errorMessage = "Failed to extract details. Please try again.";

			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as AxiosError;
				if (axiosError.response?.status === 501) {
					const customError = errorCheck();
					errorMessage =
						customError || "Please Check The Document. Upload Again.";
				} else {
					errorMessage = "Please Check The Document. Upload Again.";
				}
			}

			setError(errorMessage);
		} finally {
			setIsStatusUpdating(false);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile) return setError("Please select a file.");

		setIsUploading(true);

		const uploadToBucket = async (file: File) => {
			const { presignedUrl, documentId } = await getPresignedUrl(file);

			await fetch(presignedUrl, {
				method: "PUT",
				body: file,
				headers: {
					"Content-Type": file?.type || "application/octet-stream",
				},
			});

			return documentId;
		};

		const updateDocumentStatus = async (documentId: string) => {
			return axiosInstance.patch(
				`/documents/update/requested/document/status/${documentId}`,
				{
					applicationId,
					userId: user?.uid,
					status: "under_review",
				},
			);
		};

		try {
			// PAYMENT FLOW
			if (from === "payment") {
				try {
					const documentId = await uploadToBucket(selectedFile);
					await updateDocumentStatus(documentId);
					showToast("Payment document uploaded successfully!", "success");
					onUploadSuccess?.();
				} catch (e) {
					console.error("Payment upload failed:", e);
					showToast("Payment upload failed. Please try again.", "error");
				}
				return;
			}

			// SKIP OCR FLOW
			if (skipOCR) {
				try {
					const documentId = await uploadToBucket(selectedFile);
					await updateDocumentStatus(documentId);
					showToast("Document uploaded successfully!", "success");

					removeFile();
					onClose();
					onUploadSuccess?.();
				} catch (e) {
					console.error("Upload failed:", e);
					showToast("Upload failed. Please try again.", "error");
				}
				return;
			}

			// OCR FLOW
			try {
				const docType = documentName.toLowerCase().replace(/\s+/g, "_");
				if (!OCR_SUPPORTED_DOC_TYPES.has(docType)) {
					const documentId = await uploadToBucket(selectedFile);
					await updateDocumentStatus(documentId);
					showToast("Document uploaded successfully!", "success");
					removeFile();
					onClose();
					onUploadSuccess?.();
					return;
				}

				await extractDetails(selectedFile, docType);
			} catch (e) {
				console.error("Extraction failed:", e);
				showToast("Extraction failed. Please try again.", "error");
			}
		} finally {
			setIsUploading(false);
		}
	};

	const onUploadClick = async () => {
		if (isUploading || !selectedFile) return;

		if (onFileAccepted && selectedFile) {
			setIsUploading(true);
			setShowPreview(false);
			setPreviewUrl(null);
			try {
				await onFileAccepted(selectedFile);
				removeFile();
			} finally {
				setIsUploading(false);
			}
			return;
		}
		await handleUpload();
	};

	const handleConfirmUpdate = async () => {
		const hasErrors = Object.values(editErrors).some((e) => e);
		if (hasErrors) return;

		setError("");
		setIsLoading(true);

		try {
			await uploadFileToFirebase();
		} catch (error) {
			console.error("Upload failed:", error);
			showToast("Upload failed. Please try again.", "error");
			setIsLoading(false);
			return;
		}

		try {
			const docType = documentName.toLowerCase();
			let payload: any = {};

			// PASSPORT PAYLOAD
			if (docType.includes("passport")) {
				const firstName = capitalFirstLetter(
					extractedData?.given_name?.toString() || "",
				);
				const lastName = capitalFirstLetter(
					(extractedData?.surname || extractedData?.surename)?.toString() || "",
				);
				const fullName = `${firstName} ${lastName}`.trim();
				const parsedName = parseExtractedName(fullName);
				const parsedAddress = parseAddress(
					extractedData?.address?.toString() || "",
				);

				payload = {
					studentPassportInfo: {
						passportNumber: extractedData?.passport_number || "",
						countryOfIssue: "India",
						expiryDate: formatToDDMMYYYY(extractedData?.date_of_expiry),
					},
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						// studentTitle: userDetails?.student_metadata?.student_title || "",
						// fatherTitle: userDetails?.student_metadata?.father_title || "",
						gender: extractedData?.gender
							? extractedData.gender === "F"
								? "Female"
								: extractedData.gender === "M"
									? "Male"
									: extractedData.gender
							: userDetails?.gender || null,
						dob: extractedData?.date_of_birth
							? parseDate(extractedData.date_of_birth.toString())
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							extractedData?.name_of_father_or_legal_guardian?.toString() ||
								userDetails?.father_name ||
								"",
						),
					},
					studentAddressInfo: extractedData?.address
						? {
								addressLine1: parsedAddress.addressLine1,
								addressLine2: parsedAddress.addressLine2,
								countryName: parsedAddress.countryName,
								stateName: parsedAddress.stateName,
								cityName: parsedAddress.cityName,
								pincode: parsedAddress.pincode,
							}
						: undefined,
				};
			}

			// ENGLISH PROFICIENCY PAYLOAD
			if (docType.includes("english")) {
				let validTillDate = "";
				if (extractedData?.test_date) {
					// Use valid_till_date from extractedData if it's already set (e.g. from UI edit)
					// otherwise calculate it from test_date
					if (extractedData.valid_till_date) {
						validTillDate = formatToDDMMYYYY(extractedData.valid_till_date);
					} else {
						const date = parseDate(String(extractedData.test_date));
						if (date && !isNaN(date.getTime())) {
							const validDate = new Date(date);
							validDate.setFullYear(validDate.getFullYear() + 2);
							validTillDate = formatToDDMMYYYY(validDate);
						}
					}
				}

				const sectionScores =
					typeof extractedData?.section_scores === "object" &&
					extractedData?.section_scores !== null
						? (extractedData.section_scores as SectionScores)
						: undefined;

				payload = {
					studentLanguageTestInfo: [
						{
							testName: extractedData?.test_type || "",
							overall: parseFloat(
								typeof extractedData?.overall_score === "number"
									? extractedData.overall_score.toString()
									: typeof extractedData?.overall_score === "string"
										? extractedData.overall_score
										: (extractedData?.overall as string) || "0",
							),
							reading: parseFloat(
								sectionScores?.reading ||
									(extractedData?.reading as string) ||
									"0",
							),
							writing: parseFloat(
								sectionScores?.writing ||
									(extractedData?.writing as string) ||
									"0",
							),
							speaking: parseFloat(
								sectionScores?.speaking ||
									(extractedData?.speaking as string) ||
									"0",
							),
							listening: parseFloat(
								sectionScores?.listening ||
									(extractedData?.listening as string) ||
									"0",
							),
							validTillDate: validTillDate,
						},
					],
				};
			}

			// MOI PROFICIENCY PAYLOAD
			if (docType.includes("moi")) {
				payload = {
					studentLanguageTestInfo: [
						{
							testName: "I-DON'T-HAVE-ANY",
							boardName: extractedData?.board_name || "",
							englishMarks: Number(extractedData?.english_marks) || 0,
							overall: Number(extractedData?.english_marks) || 0,
						},
					],
				};
			}

			// 10TH MARKSHEET PAYLOAD
			if (docType.includes("10th")) {
				const parsedName = parseExtractedName(
					(
						extractedData?.studentName ||
						extractedData?.student_name ||
						""
					).toString(),
				);
				const outOfValue =
					Number(extractedData?.outOf || extractedData?.overall_marks_out_of) ||
					100;
				const dobValue = (
					extractedData?.dateOfBirth ||
					extractedData?.date_of_birth ||
					""
				).toString();

				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "10th",
							instituteName: extractedData?.institution || "",
							boardName: extractedData?.board || "",
							completionYear: extractedData?.yearOfPassing || "",
							gradeType: "Percentage",
							cgpaOrPercentageValue:
								Number(extractedData?.cgpaOrPercentageValue) || 0,
							outOf: outOfValue,
							subjectSpecificMarks: extractedData?.subjectSpecificMarks || {
								additionalSubject: {},
							},
						},
					],
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						// studentTitle: userDetails?.student_metadata?.student_title || "",
						fatherTitle: userDetails?.student_metadata?.father_title || "",
						gender: userDetails?.gender || null,
						dob: dobValue
							? parseDate(dobValue)
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							(
								extractedData?.fathersName ||
								extractedData?.fathers_or_guardians_name ||
								userDetails?.father_name ||
								""
							).toString(),
						),
					},
				};
			}

			// 12TH MARKSHEET PAYLOAD
			if (docType.includes("12th")) {
				const parsedName = parseExtractedName(
					(
						extractedData?.studentName ||
						extractedData?.student_name ||
						""
					).toString(),
				);
				const outOfValue =
					Number(extractedData?.outOf || extractedData?.overall_marks_out_of) ||
					100;
				const dobValue = (
					extractedData?.dateOfBirth ||
					extractedData?.date_of_birth ||
					""
				).toString();

				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "12th",
							instituteName: extractedData?.institution || "",
							boardName: extractedData?.board || "",
							completionYear: extractedData?.yearOfPassing || "",
							gradeType: "Percentage",
							cgpaOrPercentageValue:
								Number(extractedData?.cgpaOrPercentageValue) || 0,
							outOf: outOfValue,
							fieldOfStudy: extractedData?.fieldOfStudy || "",
							subjectSpecificMarks: extractedData?.subjectSpecificMarks || {
								additionalSubject: {},
							},
						},
					],
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						// studentTitle: userDetails?.student_metadata?.student_title || "",
						fatherTitle: userDetails?.student_metadata?.father_title || "",
						gender: userDetails?.gender || null,
						dob: dobValue
							? parseDate(dobValue)
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							(
								extractedData?.fathersName ||
								extractedData?.fathers_or_guardians_name ||
								userDetails?.father_name ||
								""
							).toString(),
						),
					},
				};
			}

			// BACHELOR'S DEGREE PAYLOAD
			if (docType.includes("bachelor")) {
				const gradeInfo = calculateBachelorGrading(extractedData || {});

				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "Bachelor",
							instituteName:
								extractedData?.institution_name ||
								extractedData?.institution ||
								"",
							qualificationName:
								extractedData?.degree_name || extractedData?.degreeName || "",
							completionYear: String(
								extractedData?.passout_year ||
									extractedData?.yearOfPassing ||
									"",
							),
							gradeType: gradeInfo.gradeType,
							cgpaOrPercentageValue: Number(gradeInfo.value) || 0,
							outOf: Number(gradeInfo.outOf) || 100,
						},
					],
				};
			}

			// UPDATE STUDENT DETAILS IF PAYLOAD EXISTS
			if (Object.keys(payload).length > 0) {
				try {
					await dispatch(
						updateUserDetails({
							payload,
							id: studentId || user?.uid || "",
						}),
					);

					// Auto-sync savedEligibility when academic or English docs are uploaded
					const effectiveUserId = studentId || user?.uid || "";
					if (effectiveUserId && (docType.includes("10th") || docType.includes("12th") || docType.includes("bachelor") || docType.includes("english") || docType.includes("moi"))) {
						try {
							const qualInfo = payload.studentQualificationInfo?.[0];
							const langInfo = payload.studentLanguageTestInfo?.[0];
							const eligibilityPayload: Record<string, string | number> = {};

							if (qualInfo) {
								eligibilityPayload.pastQualification = qualInfo.qualificationLevel ?? "";
								eligibilityPayload.courseStream = qualInfo.fieldOfStudy ?? "";
								eligibilityPayload.qualificationYear = qualInfo.completionYear ?? "";
								eligibilityPayload.gradingSystem = qualInfo.gradeType ?? "Percentage";
								eligibilityPayload.pastQualificationPercentage = Number(qualInfo.cgpaOrPercentageValue) || 0;
							}

							if (langInfo) {
								const isNoTest = langInfo.testName?.toUpperCase().replace(/[^A-Z]/g, "") === "IDONTHAVEANY";
								if (isNoTest) {
									eligibilityPayload.test = "";
									eligibilityPayload.boardName = langInfo.boardName ?? "";
									eligibilityPayload.overall = langInfo.englishMarks ?? 0;
								} else {
									eligibilityPayload.test = langInfo.testName ?? "";
									eligibilityPayload.listening = langInfo.listening ?? 0;
									eligibilityPayload.reading = langInfo.reading ?? 0;
									eligibilityPayload.writing = langInfo.writing ?? 0;
									eligibilityPayload.speaking = langInfo.speaking ?? 0;
									eligibilityPayload.overall = langInfo.overall ?? 0;
								}
							}

							if (Object.keys(eligibilityPayload).length > 0) {
								dispatch(saveEligibilityDetails({ eligibility: eligibilityPayload, studentId: effectiveUserId }));
							}
						} catch {
							// Best-effort — don't block upload success
						}
					}
				} catch (error) {
					console.error("Updating student details failed:", error);
					showToast("Failed to update student details.", "error");
				}
			}

			setEditErrors({});
			removeFile();
			setShowConfirmPopup(false);
			onClose();
			showToast("Document uploaded successfully!", "success");
			void dispatch(getUploadedDocument(user?.uid ?? ""));
			if (user?.uid) {
				void dispatch(getUserDetail(user.uid));
			}
			void onUploadSuccess?.();
		} catch (error) {
			console.error("Details update failed:", error);
			showToast("Failed to update details.", "error");
		} finally {
			setIsLoading(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-[1000]">
			<div className="bg-white w-full max-w-[650px] rounded-lg shadow-lg relative">
				<div className="flex justify-between items-center border-b p-4">
					<h2 className="text-sm sm:text-lg md:text-xl font-semibold text-black">
						Upload Document
					</h2>
					<button
						onClick={() => onClose()}
						className="hover:text-black cursor-pointer">
						<X size={18} />
					</button>
				</div>

				<div className="p-3 sm:p-6 ff_poppins">
					<p className="text-xs sm:text-sm md:text-base sm:leading-[24px] font-medium text-[#364261]">
						Upload your document, and our system will automatically detect its
						type for a seamless experience. Accepted file types include PDF and
						docx, with a maximum file size of 5 MB.
					</p>

					{showTitle && (
						<div className="my-4 sm:m-4 bg-blue-50 p-2 rounded text-blue-600 text-sm font-medium">
							Uploading: {documentName}
						</div>
					)}

					<DropzoneUploader
						dragActive={dragActive}
						setDragActive={setDragActive}
						selectedFile={selectedFile}
						onFileSelect={handleFile}
						triggerFileInput={triggerFileInput}
						fileInputRef={fileInputRef}
					/>

					{selectedFile && (
						<FilePreviewCard
							file={selectedFile}
							onRemove={removeFile}
							onPreview={(url) => {
								setPreviewUrl(url);
								setShowPreview(true);
							}}
						/>
					)}

					{error && (
						<div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
							{error}
						</div>
					)}
				</div>
				<div className="flex gap-3 p-4 border-t">
					<button
						onClick={() => {
							removeFile();
							onClose();
						}}
						className="py-2 px-4 border border-[#0162E8] rounded-[6px] text-[#0162E8] w-full hover:bg-black-50 cursor-pointer"
						disabled={isUploading}>
						Cancel
					</button>
					<button
						onClick={onUploadClick}
						className={`py-2 px-4 border border-[#0162E8] rounded-[6px] bg-[#0162E8] flex items-center gap-2 w-full justify-center  ${
							!selectedFile || isUploading
								? "opacity-50 cursor-not-allowed"
								: "hover:bg-[#0051c9] cursor-pointer"
						}`}
						disabled={!selectedFile || isUploading}>
						<p className="text-white ff_poppins text-sm leading-[21px]">
							{isUploading ? "Uploading..." : "Upload"}
						</p>
					</button>
				</div>

				{isStatusUpdating && (
					<div className="absolute inset-0 flex items-center justify-center rounded-lg z-10 bg-[#dfdfdf]">
						<div className="flex flex-col items-center text-center px-4">
							<Image
								className="w-full max-w-[300px] sm:max-w-[400px] pointer-events-none"
								src={"/assets/gif/extraction.gif"}
								alt="loading"
								width={200}
								height={200}
							/>
							<div className="mt-4 ff_poppins">
								<p className="text-base sm:text-lg font-semibold text-gray-800">
									Extracting your details...
								</p>
								<p className="mt-1 max-w-[360px] text-xs sm:text-sm text-gray-600">
									We&apos;re pulling information from your documents to pre-fill
									your application fields for you.
								</p>
							</div>
						</div>
					</div>
				)}
			</div>

			{showPreview && previewUrl && selectedFile && (
				<DocumentPreviewModal
					file={selectedFile}
					previewUrl={previewUrl}
					onClose={() => {
						setShowPreview(false);
						setPreviewUrl(null);
					}}
				/>
			)}

			{showConfirmPopup && extractedData && (
				<ConfirmExtractedDetails
					documentName={documentName}
					extractedData={extractedData}
					editErrors={editErrors}
					isLoading={isLoading}
					onChange={(field, value) => {
						const sectionFields = [
							"listening",
							"reading",
							"writing",
							"speaking",
						];

						if (sectionFields.includes(field)) {
							setExtractedData((prev) => ({
								...(prev ?? {}),
								section_scores: {
									...(prev &&
									typeof prev.section_scores === "object" &&
									prev.section_scores !== null
										? prev.section_scores
										: {}),
									[field]: value,
								},
							}));
						} else if (field === "test_date") {
							// Auto-calculate valid_till_date
							let validTillDate = "";
							if (value) {
								const date = parseDate(value);
								if (date && !isNaN(date.getTime())) {
									const validDate = new Date(date);
									validDate.setFullYear(validDate.getFullYear() + 2);
									validTillDate = formatToDDMMYYYY(validDate);
								}
							}

							setExtractedData((prev) => ({
								...prev,
								[field]: value,
								valid_till_date: validTillDate,
							}));
						} else {
							setExtractedData((prev) => ({ ...prev, [field]: value }));
						}
					}}
					onConfirm={handleConfirmUpdate}
					onClose={() => {
						setShowConfirmPopup(false);
						removeFile();
						onClose();
					}}
				/>
			)}
		</div>
	);
}
